import React, { useState, useEffect } from 'react';
import { useNavigate as useRouterNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Snackbar,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { menuApi, type CreateMenuDto, type UpdateMenuDto } from '../../api/menuApi';

type MenuFormData = Omit<CreateMenuDto, 'parentId'> & {
  parentId: string | null;
};

const MenuForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useRouterNavigate();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuGroups, setMenuGroups] = useState<Array<{ id: string; title: string }>>([]);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'info' | 'warning' 
  });
  
  const [formData, setFormData] = useState<MenuFormData>({
    title: '',
    path: '',
    icon: '',
    order: 0,
    isGroup: false,
    isActive: true, // Default to active when creating a new menu
    parentId: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch menu groups for parent selection using getMenuTree
        const menuTree = await menuApi.getMenuTree();
        // Filter to get only the groups (isGroup: true)
        const groups = menuTree.filter((item) => item.isGroup);
        setMenuGroups(groups);

        // If in edit mode, fetch the menu item data
        if (isEditMode && id) {
          const menu = await menuApi.menus.getById(id);
          setFormData({
            title: menu.title,
            path: menu.path,
            icon: menu.icon || '',
            order: menu.order,
            isGroup: menu.isGroup,
            isActive: menu.isActive,
            parentId: menu.parentId,
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
        setError(errorMessage);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load menu data: ' + errorMessage, 
          severity: 'error' 
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (isEditMode && id) {
        await menuApi.menus.update(id, formData);
        setSnackbar({ 
          open: true, 
          message: 'Menu updated successfully', 
          severity: 'success' 
        });
        // Navigate back after a short delay
        setTimeout(() => navigate('/settings/menus'), 1000);
      } else {
        await menuApi.menus.create(formData);
        setSnackbar({ 
          open: true, 
          message: 'Menu created successfully', 
          severity: 'success'
        });
        // Navigate back after a short delay
        setTimeout(() => navigate('/settings/menus'), 1000);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save menu';
      setError(errorMessage);
      setSnackbar({ 
        open: true, 
        message: `Failed to save menu: ${errorMessage}`, 
        severity: 'error' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box mb={3} display="flex" alignItems="center">
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/settings/menus')}
          sx={{ mr: 2 }}
        >
          Back to List
        </Button>
        <Typography variant="h5">
          {isEditMode ? 'Edit Menu' : 'Create New Menu'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Path"
                name="path"
                value={formData.path}
                onChange={handleChange}
                required={!formData.isGroup}
                disabled={formData.isGroup}
                margin="normal"
                helperText={formData.isGroup ? 'Group menus don\'t have paths' : 'e.g., /dashboard'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Icon"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                margin="normal"
                helperText="Icon name (e.g., DashboardIcon)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                name="order"
                value={formData.order}
                onChange={handleChange}
                margin="normal"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => {
                      console.log('isActive changed:', e.target.checked);
                      setFormData(prev => ({
                        ...prev,
                        isActive: e.target.checked
                      }));
                    }}
                    name="isActive"
                    color="primary"
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="parent-menu-label">Parent Menu (Optional)</InputLabel>
                <Select
                  labelId="parent-menu-label"
                  name="parentId"
                  value={formData.parentId || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    parentId: e.target.value || null
                  }))}
                  label="Parent Menu"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {menuGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isGroup}
                    onChange={handleCheckboxChange}
                    name="isGroup"
                    color="primary"
                  />
                }
                label="This is a menu group (can contain other menus)"
              />
              <FormHelperText>
                Menu groups are used to create dropdown menus in the navigation
              </FormHelperText>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={submitting}
                >
                  {isEditMode ? 'Update' : 'Create'} Menu
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity as 'success' | 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MenuForm;
